import React, { useContext, useCallback, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { css } from '@emotion/react';
import {
    Checkbox,
    Badge,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Switch,
    TextField,
    IconButton,
    CircularProgress,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import { Context, ContextProvider } from './store';
import { Body1, H1, FilterButton } from './blocks';
import OrderPODModal from '@/components/CarrierAccountingOrders/modals/OrderPODModal';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import { colors } from '@/styles';
import CustomizableTable from '@/components/CustomizableTable';
import { useOrderTableProfiles } from '@/hooks';
import CustomizableTableProfileControl from '@/components/CustomizableTable/CustomizableTableProfileControl';
import Snackbar from '@/components/Snackbar';
import AccessorialAuditModal from './modals/AccessorialAuditModal';
import CompletedOrdersFilterDialog from './modals/CompletedOrdersFilterDialog';
import StatusFilter from './StatusFilter';
import Footer from './Footer';
import { withWarehouseEstimateEvent } from '@/utilities/warehouseEvents';

const DEFAULT_TABLE_INITIAL_STATE = {
    hiddenColumns: [],
    columnOrder: [],
    columnResizing: { columnWidths: {} },
};

const CompletedOrders = ({ context, title, TopBar, Footer }) => {
    const { state, loading, callbacks } = useContext(context);
    const { upsertOrder } = callbacks;
    const [tableInitialState, setTableInitialState] = useState(DEFAULT_TABLE_INITIAL_STATE);
    const [tableKey, setTableKey] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [tableLayoutState, setTableLayoutState] = useState({
        hiddenColumns: [],
        columnOrder: [],
        columnWidths: {},
    });

    const activeFilterCount = useMemo(() => {
        const f = state?.filter || {};
        const d = state?.defaultFilter || {};
        const sameTime = (a, b) => (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
        const dateActive =
            !!f.week || !sameTime(f.completedStart, d.completedStart) || !sameTime(f.completedEnd, d.completedEnd);
        let count = 0;
        if (f.shippers?.length) count += 1;
        if (f.carriers?.length) count += 1;
        if (dateActive) count += 1;
        return count;
    }, [state?.filter, state?.defaultFilter]);

    const buildProfileSettings = useCallback(() => {
        return {
            layout: {
                hiddenColumns: tableLayoutState.hiddenColumns || [],
                columnOrder: tableLayoutState.columnOrder || [],
                columnWidths: tableLayoutState.columnWidths || {},
            },
            sorting: {
                sortBy: state?.sortBy?.id ? [{ id: state.sortBy.id, desc: !!state.sortBy.desc }] : [],
            },
            filters: {
                values: {
                    stage: state?.filter?.stage,
                    completedStart: state?.filter?.completedStart || null,
                    completedEnd: state?.filter?.completedEnd || null,
                    shippers: state?.filter?.shippers || [],
                    searchTerm: state?.filter?.searchTerm || '',
                    weekNumber: state?.filter?.week?.week_number || null,
                },
            },
        };
    }, [state?.filter, state?.sortBy, tableLayoutState]);

    const applyProfileSettings = useCallback(
        (settings = {}) => {
            const layout = settings?.layout || {};
            setTableInitialState({
                hiddenColumns: layout.hiddenColumns || [],
                columnOrder: layout.columnOrder || [],
                columnResizing: { columnWidths: layout.columnWidths || {} },
            });
            setTableKey((k) => k + 1);

            const nextSort = settings?.sorting?.sortBy?.[0];
            if (nextSort?.id) {
                callbacks.setSortBy({ id: nextSort.id, desc: !!nextSort.desc });
            } else {
                callbacks.setSortBy({ id: 'completed_date', desc: true });
            }

            const nextValues = settings?.filters?.values || {};
            const nextWeek =
                state?.weeks?.find((w) => w.week_number === nextValues.weekNumber) ||
                state?.defaultFilter?.week ||
                undefined;

            callbacks.setFilter((prev) => ({
                ...prev,
                ...(state.defaultFilter || {}),
                stage: nextValues.stage || state.defaultFilter?.stage,
                completedStart: nextValues.completedStart ?? state.defaultFilter?.completedStart ?? null,
                completedEnd: nextValues.completedEnd ?? state.defaultFilter?.completedEnd ?? null,
                shippers: nextValues.shippers || [],
                searchTerm: nextValues.searchTerm || '',
                week: nextValues.weekNumber ? nextWeek : undefined,
            }));
        },
        [callbacks, state?.weeks, state?.defaultFilter]
    );

    const resetCompletedOrdersToDefaults = useCallback(() => {
        setTableInitialState(DEFAULT_TABLE_INITIAL_STATE);
        setTableKey((k) => k + 1);
        callbacks.setFilter({ ...(state.defaultFilter || {}) });
        callbacks.setSortBy({ id: 'completed_date', desc: true });
    }, [callbacks, state.defaultFilter]);

    const {
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,
        profileSelectProps,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleDeleteCurrentProfile,
        handleImportProfileById,
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,
    } = useOrderTableProfiles({
        tableType: 'COMPLETED_ORDERS',
        userId: state.userId,
        buildProfileSettings,
        applyProfileSettings,
        onResetToDefault: resetCompletedOrdersToDefaults,
        notifyError: (message) => callbacks.setNotification({ severity: 'error', message }),
    });

    const profileControls = useMemo(
        () => (
            <CustomizableTableProfileControl
                disabled={profilesLoading || loading.action}
                tableType="COMPLETED_ORDERS"
                currentProfileName={currentProfileName}
                currentProfileId={currentProfileId}
                selectedProfileId={selectedProfileId}
                onSaveNew={handleSaveNewProfile}
                onOverwrite={handleOverwriteProfile}
                onImportById={handleImportProfileById}
                onDeleteCurrent={handleDeleteCurrentProfile}
                saving={insertProfileLoading}
                overwriting={updateProfileLoading}
                deleting={deleteProfileLoading}
            />
        ),
        [
            currentProfileId,
            currentProfileName,
            deleteProfileLoading,
            handleDeleteCurrentProfile,
            handleImportProfileById,
            handleOverwriteProfile,
            handleSaveNewProfile,
            insertProfileLoading,
            loading.action,
            profilesLoading,
            selectedProfileId,
            updateProfileLoading,
        ]
    );

    const onSaveOrder = useCallback(
        ({
            errors,
            order: modified,
            items: itemsModified,
            itemsRemoved,
            itemUpdates,
            tags,
            tagsRemoved,
            documents,
            orderIds,
            subscriptions,
            services,
            servicesRemoved,
        }) => {
            if (errors?.hasMissingField?.length) {
                callbacks.setNotification({
                    message: `Order has missing fields: ${errors.hasMissingField.join(', ')}.`,
                    severity: 'error',
                });
                return;
            }
            if (errors?.phoneInvalid) {
                callbacks.setNotification({ message: 'Order has an invalid phone number.', severity: 'error' });
                return;
            }

            const { notes, ...rest } = modified;

            upsertOrder({
                variables: {
                    orders: [
                        {
                            ...withWarehouseEstimateEvent(rest),
                            notes: {
                                data: notes,
                                on_conflict: {
                                    constraint: 'notes_pkey',
                                    update_columns: ['note'],
                                },
                            },
                        },
                    ],
                    items: itemsModified || [],
                    removals: itemsRemoved || [],
                    item_updates: itemUpdates || [],
                    tags: tags,
                    tag_removals: tagsRemoved,
                    order_ids: orderIds,
                    documents: documents || [],
                    email_subscriptions: subscriptions || [],
                    services: services || [],
                    service_removals: servicesRemoved || [],
                },
            });

            callbacks.editOrder(null);
        },
        [upsertOrder, callbacks]
    );

    return (
        <>
            <Snackbar
                open={!!state.notification.message}
                handleClose={() => callbacks.setNotification({})}
                severity={state.notification.severity}
                message={state.notification.message}
            />
            <Grid
                direction="column"
                container
                css={css`
                    width: 100%;
                    max-width: 100%;
                    height: 100%;
                    min-width: 0;
                    overflow-x: hidden;
                `}
            >
                <TopBar state={state} loading={loading} callbacks={callbacks} />

                <Grid
                    item
                    css={css`
                        padding: 40px 40px 0;
                    `}
                >
                    <H1>{title}</H1>
                </Grid>

                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        padding: 16px 40px 0;
                        margin-bottom: 12px;
                        align-items: center;
                        flex-wrap: nowrap;
                        min-width: 0;
                        column-gap: 16px;
                    `}
                >
                    <Grid
                        item
                        css={css`
                            flex: 0 0 auto;
                            width: 280px;
                        `}
                    >
                        <TextField
                            select
                            variant="outlined"
                            fullWidth
                            label={profiles?.length ? 'Profile' : 'Select Profile...'}
                            InputLabelProps={{ shrink: true }}
                            {...profileSelectProps}
                            css={css`
                                background-color: white;
                                width: 100%;
                            `}
                        >
                            <MenuItem value="">Default</MenuItem>
                            {profiles.map((profile) => (
                                <MenuItem key={profile.order_table_profile_id} value={profile.order_table_profile_id}>
                                    <Body1>{profile.name}</Body1>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            flex: 0 0 auto;
                        `}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!state.routeRatesEnabled}
                                    onChange={(e) => callbacks.setRouteRatesEnabled(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Group by Route"
                            css={css`
                                margin: 0;
                                .MuiFormControlLabel-label {
                                    white-space: nowrap;
                                }
                            `}
                        />
                    </Grid>

                    <Grid
                        item
                        css={css`
                            flex: 1 1 auto;
                            min-width: 16px;
                        `}
                    />

                    <Grid
                        item
                        css={css`
                            flex: 0 1 360px;
                            min-width: 0;
                        `}
                    >
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Search"
                            placeholder="Search..."
                            value={state?.filter?.searchTerm || ''}
                            onChange={(e) => {
                                callbacks.setFilter((prev) => {
                                    return {
                                        ...prev,
                                        searchTerm: e.target.value,
                                    };
                                });
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            css={css`
                                background-color: white;
                                width: 100%;
                            `}
                        />
                    </Grid>
                    <Grid
                        item
                        css={css`
                            flex: 0 0 auto;
                        `}
                    >
                        <Badge badgeContent={activeFilterCount} color="primary">
                            <FilterButton startIcon={<FilterListIcon />} onClick={() => setFiltersOpen(true)}>
                                Filters
                            </FilterButton>
                        </Badge>
                    </Grid>
                </Grid>

                <Grid
                    item
                    css={css`
                        overflow: hidden;
                        flex: 1 1 0;
                        min-width: 0;
                        min-height: 0;
                        max-width: 100%;
                        padding: 0 40px;
                        position: relative;
                        display: flex;
                        flex-direction: column;
                    `}
                >
                    <div
                        css={css`
                            flex: 1 1 0;
                            min-width: 0;
                            min-height: 0;
                            display: flex;
                            flex-direction: column;
                            overflow: hidden;
                        `}
                    >
                        <CustomizableTable
                            key={tableKey}
                            data={state.orders}
                            columns={state.columns}
                            callbacks={callbacks}
                            enableRowSelection={true}
                            selectedRowIds={state.selectedOrderIds}
                            onSelectionChange={callbacks.setSelectedOrderIds}
                            selectionController={state.selectionController}
                            sortBy={state.sortBy ? [state.sortBy] : []}
                            onSortChange={(newSortBy) => {
                                const next = newSortBy || { id: 'completed_date', desc: true };
                                if (next.id !== state.sortBy?.id || next.desc !== state.sortBy?.desc) {
                                    callbacks.setSortBy(next);
                                }
                            }}
                            onLoadMore={callbacks.loadMore}
                            hasMore={state.hasMore}
                            isLoading={loading.init}
                            getRowId={callbacks.getRowId}
                            initialState={tableInitialState}
                            onLayoutChange={setTableLayoutState}
                            wrapBodyCellContent={true}
                            manualSortBy={!!state.routeRatesEnabled}
                        />
                    </div>
                    {loading.editOrder && (
                        <div
                            css={css`
                                position: absolute;
                                top: 0;
                                left: 0;
                                right: 0;
                                bottom: 0;
                                background-color: rgba(255, 255, 255, 0.7);
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                z-index: 10;
                            `}
                        >
                            <CircularProgress
                                size={48}
                                css={css`
                                    color: ${colors.greys[4]};
                                `}
                            />
                        </div>
                    )}
                </Grid>

                <Footer state={state} loading={loading} callbacks={callbacks} profileControls={profileControls} />
            </Grid>

            <EditOrderModal
                order={state.editingOrder}
                open={!!state.editingOrder}
                callbacks={{
                    onSave: onSaveOrder,
                    onClose: () => callbacks.editOrder(null),
                    onError: (error) => {
                        callbacks.setNotification({
                            message: error?.message || error || `We're sorry, something went wrong.`,
                            severity: 'error',
                        });
                    },
                }}
            />

            <OrderPODModal
                order={state.viewPod}
                callbacks={{
                    onClose: () => {
                        callbacks.viewOrderPod(null);
                    },
                }}
            />

            {state.auditEnabled && state.invoicingPermissions && state.auditSession && (
                <AccessorialAuditModal
                    open={!!state.auditSession}
                    mode={state.auditSession.mode}
                    route={state.auditSession.route}
                    orders={state.auditSession.orders || []}
                    onClose={callbacks.closeAuditModal}
                    onApprove={(orderIds) => {
                        callbacks.approveOrderIds(orderIds);
                    }}
                    onApproveOne={callbacks.approveAuditOrder}
                    canEditPerspective={callbacks.canEditPerspective}
                    showAllBreakdowns={state.canViewAllBreakdowns}
                />
            )}

            <CompletedOrdersFilterDialog
                open={filtersOpen}
                state={state}
                callbacks={callbacks}
                onClose={() => setFiltersOpen(false)}
            />
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} context={Context} title="Completed Orders" TopBar={StatusFilter} Footer={Footer} />
        </ContextProvider>
    );

export default compose(withContext)(CompletedOrders);
